import ClassroomManagers from './components/ClassroomManagers';
import ClassroomProfessor from './components/ClassroomProfessor';
import ClassroomSupplementarMaterial from './components/ClassroomSupplementarMaterial';
import useNotification from 'hooks/useNotification';
import { Button, Flex, Form, Tabs, Tag } from 'antd';
import { ClassroomCalendar } from './components/ClassroomCalendar';
import { ClassroomContent } from './components/ClassroomContent';
import { ClassroomEnrollments } from './components/ClassroomEnrollments';
import { ClassroomEvaluations } from './components/ClassroomEvaluations';
import { ClassroomForum } from './components/Forum/ClassroomForum';
import { ClassroomStudentsAvailableForEnrollment } from './components/ClassroomStudentsAvailableForEnrollment';
import { CoursesIconComponent } from 'assets/icons/courses';
import { CreateTopicModal } from 'components/modal/forum/CreateTopicModal';
import {
  EditClassroomForm,
  IClassroomPost
} from 'components/classroom/EditClassroomForm';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { HttpError, useCan } from '@refinedev/core';
import { ITree } from 'interfaces/tree';
import { PageHeader } from 'components/professor_area/PageHeader';
import { PlusSquareOutlined } from '@ant-design/icons';
import { formatDate } from 'services/date';
import { handleApiError } from '../../utils/error-handler';
import { isClassroomForumEnabled } from 'services/tenants';
import { parseClassroomFormValues } from 'utils/classroom-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCustomSearchParams } from 'hooks/useSearchParams';
import { useForm, useModal } from '@refinedev/antd';

export const ClassroomEdit = () => {
  const { currentSearchParams: currentTab, setSearchParams } =
    useCustomSearchParams({
      searchParams: 'tab'
    });
  const [activeTab, setActiveTab] = useState(currentTab || 'info');
  const [contentTree, setContentTree] = useState<ITree>();
  const { showSuccess } = useNotification();
  const classroomForumEnabled = isClassroomForumEnabled();
  const { data: canShowClassroom } = useCan({
    resource: 'classrooms',
    action: 'show'
  });
  const { data: canEditClassroom } = useCan({
    resource: 'classrooms',
    action: 'edit'
  });
  const { data: canListEvaluations } = useCan({
    resource: 'classroom_evaluations',
    action: 'list'
  });
  const { data: canListEnrollments } = useCan({
    resource: 'classroom_enrollments',
    action: 'list'
  });
  const { data: canListProfessors } = useCan({
    resource: 'classroom_professors',
    action: 'list'
  });
  const { data: canListThreads } = useCan({
    resource: 'discussion_threads',
    action: 'list'
  });

  const {
    show: topicCreateModalShow,
    close: topicCreateModalClose,
    modalProps: topicCreateModalProps
  } = useModal();

  useEffect(() => {
    if (currentTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab]);

  // Link antigo para ?tab=forum em tenant que desligou o fórum de turma
  // deixaria o painel em branco. Só checa o tenant, que é síncrono: as
  // permissões das abas ainda estão carregando neste ponto.
  useEffect(() => {
    if (activeTab === 'forum' && !classroomForumEnabled) {
      setActiveTab('info');
      setSearchParams('info');
    }
  }, [activeTab, classroomForumEnabled, setSearchParams]);

  const onTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setSearchParams(tab);
    },
    [setSearchParams]
  );

  const { formProps, saveButtonProps, queryResult, onFinish } = useForm<
    IClassroomPost,
    HttpError,
    IClassroomPost
  >({
    redirect: 'edit',
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: () => {
      showSuccess('Turma editada com sucesso');
    },
    onMutationError: handleApiError
  });

  const [contentForm] = Form.useForm<IClassroomPost>();

  const classroom = queryResult?.data?.data;

  const handleOnFinish = useCallback(
    async (values: IClassroomPost) => {
      const parsedValues = parseClassroomFormValues(values, contentTree);

      try {
        await onFinish(parsedValues);
      } catch {}
    },
    [contentTree, onFinish]
  );

  const handleContentChange = useCallback(
    (data: ITree) => {
      setContentTree(data);
      queryResult?.refetch();
    },
    [queryResult]
  );

  const footerButtonStyle = useMemo(
    () => ({
      marginRight: 8,
      justifyContent: 'flex-end',
      display: ['info', 'content'].includes(activeTab) ? 'flex' : 'none'
    }),
    [activeTab]
  );

  const renderTabContent = useCallback(
    (key: string) => {
      if (!classroom) {
        return null;
      }

      switch (key) {
        case 'info': {
          return (
            <EditClassroomForm
              edit
              formProps={{
                ...formProps,
                onFinish: handleOnFinish
              }}
              discipline={{
                id: classroom.discipline.id,
                name: classroom.discipline.name
              }}
            />
          );
        }

        case 'content': {
          return (
            <Form
              {...formProps}
              form={contentForm}
              onFinish={handleOnFinish}
              layout="vertical"
            >
              <ClassroomContent
                classroom={classroom}
                onChange={handleContentChange}
              />
            </Form>
          );
        }

        case 'evaluations': {
          return (
            <ClassroomEvaluations
              disciplineId={classroom.discipline.id}
              classroomId={classroom.id}
            />
          );
        }

        case 'enrollments': {
          return (
            <ClassroomEnrollments
              classroomId={classroom.id}
              classroom={classroom}
            />
          );
        }

        case 'available': {
          return (
            <ClassroomStudentsAvailableForEnrollment
              classroomId={classroom.id}
            />
          );
        }

        case 'professor': {
          return <ClassroomProfessor classroom={classroom} />;
        }

        case 'managers': {
          return <ClassroomManagers classroom={classroom} />;
        }

        case 'supplementar_material': {
          return <ClassroomSupplementarMaterial classroom={classroom} />;
        }

        case 'forum': {
          return classroomForumEnabled ? (
            <>
              <Button
                icon={<PlusSquareOutlined />}
                type="default"
                onClick={topicCreateModalShow}
                style={{ marginBottom: 16 }}
              >
                Novo Tópico
              </Button>
              <ClassroomForum />
            </>
          ) : null;
        }

        case 'calendar': {
          return <ClassroomCalendar classroom={classroom} />;
        }

        default: {
          return null;
        }
      }
    },
    [
      classroom,
      formProps,
      contentForm,
      handleOnFinish,
      handleContentChange,
      classroomForumEnabled,
      topicCreateModalShow
    ]
  );

  return (
    <EditLayout
      title="Editar turma"
      footerButtonProps={{
        style: footerButtonStyle
      }}
      saveButtonProps={
        canEditClassroom?.can ? saveButtonProps : { hidden: true }
      }
    >
      <PageHeader
        title={`Turma: ${classroom?.code}`}
        icon={CoursesIconComponent}
        meta={
          <Flex>
            <Tag>{classroom?.discipline.name}</Tag>
            {classroom?.starts_at && classroom?.ends_at && (
              <Tag>
                {formatDate(classroom?.starts_at, 'DD/MM/YYYY')} -{' '}
                {formatDate(classroom?.ends_at, 'DD/MM/YYYY')}
              </Tag>
            )}
          </Flex>
        }
      />

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        className="custom-tabs"
      >
        {canEditClassroom?.can && (
          <Tabs.TabPane tab="Informações gerais" key="info">
            {renderTabContent('info')}
          </Tabs.TabPane>
        )}

        {canEditClassroom?.can && (
          <Tabs.TabPane tab="Conteúdo" key="content">
            {renderTabContent('content')}
          </Tabs.TabPane>
        )}

        {canListEvaluations?.can && (
          <Tabs.TabPane tab="Avaliações" key="evaluations">
            {renderTabContent('evaluations')}
          </Tabs.TabPane>
        )}

        {canListEnrollments?.can && (
          <Tabs.TabPane tab="Alunos matriculados" key="enrollments">
            {renderTabContent('enrollments')}
          </Tabs.TabPane>
        )}

        {canListEnrollments?.can && (
          <Tabs.TabPane tab="Alunos disponíveis para matrícula" key="available">
            {renderTabContent('available')}
          </Tabs.TabPane>
        )}

        {canListProfessors?.can && (
          <Tabs.TabPane tab="Professor responsável" key="professor">
            {renderTabContent('professor')}
          </Tabs.TabPane>
        )}

        {canEditClassroom?.can && (
          <Tabs.TabPane tab="Corpo docente" key="managers">
            {renderTabContent('managers')}
          </Tabs.TabPane>
        )}

        {canShowClassroom?.can && (
          <Tabs.TabPane
            tab="Materiais complementares"
            key="supplementar_material"
          >
            {renderTabContent('supplementar_material')}
          </Tabs.TabPane>
        )}

        {canListThreads?.can && classroomForumEnabled && (
          <Tabs.TabPane tab="Fóruns" key="forum">
            {renderTabContent('forum')}
          </Tabs.TabPane>
        )}

        {canShowClassroom?.can && (
          <Tabs.TabPane tab="Calendário" key="calendar">
            {renderTabContent('calendar')}
          </Tabs.TabPane>
        )}
      </Tabs>
      <CreateTopicModal
        kind="Classroom"
        parent={classroom}
        modalProps={topicCreateModalProps}
        modalClose={topicCreateModalClose}
      />
    </EditLayout>
  );
};
